import { useRef, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@paulownia/shared/components/ui/button';
import { Copy, Download } from 'lucide-react';
import { toast } from 'sonner';

const CUSTOMER_BASE_URL = 'https://order.paulownia.click';
const QR_SIZE = 200;

interface QRCodeSectionProps {
  slug: string;
  tenantName: string;
}

export function QRCodeSection({ slug, tenantName }: QRCodeSectionProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const orderUrl = `${CUSTOMER_BASE_URL}/${slug}`;

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(orderUrl);
      toast.success('Lien copié dans le presse-papier');
    } catch {
      toast.error('Impossible de copier le lien');
    }
  }, [orderUrl]);

  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `qr-code-${slug}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [slug]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-4">
        <div ref={canvasRef} className="bg-white p-4 rounded-lg border">
          <QRCodeCanvas
            value={orderUrl}
            size={QR_SIZE}
            level="H"
            marginSize={2}
          />
        </div>
        <p className="text-sm text-muted-foreground text-center break-all">
          {orderUrl}
        </p>
      </div>
      <div className="flex gap-2 justify-center">
        <Button variant="outline" size="sm" onClick={handleCopyLink} className="gap-2">
          <Copy className="h-4 w-4" />
          Copier le lien
        </Button>
        <Button variant="outline" size="sm" onClick={handleDownload} className="gap-2">
          <Download className="h-4 w-4" />
          Télécharger
        </Button>
      </div>
      <p className="text-xs text-muted-foreground text-center">
        Imprimez ce QR code sur vos affiches, flyers ou étals pour que vos clients puissent commander chez {tenantName}.
      </p>
    </div>
  );
}
